// Demo page: NextAdmin /ui-elements/dialogs (form dialog) with the select from /form-layouts.
import { useMutation, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import {
  createConversationGroupMutation,
  listConversationGroupsOptions,
  updateConversationMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { zConversationGroupCreate, zConversationUpdate } from '../../generated/api/zod.gen.js';
import { Form, FormActions, SelectFormField, TextFormField, useZodForm } from '../../components/form/index.js';
import { Modal } from '../../components/ui/Modal.js';
import { QueryBoundary } from '../../components/ui/QueryBoundary.js';

/** The select's value for "no group" (a group id is never this). */
const NO_GROUP = '__none__';

const groupSchema = zConversationUpdate
  .pick({ groupId: true })
  .extend({ newGroup: zConversationGroupCreate.shape.name.optional() });

interface Props {
  conversationId: string;
  onClose: () => void;
  onMoved: () => void;
}

/** Moves one conversation into a group, or into a new group created in the same step. */
export function ConversationGroupDialog({ conversationId, onClose, onMoved }: Props) {
  const groups = useQuery(listConversationGroupsOptions());
  const update = useMutation(updateConversationMutation());
  const createGroup = useMutation(createConversationGroupMutation());
  const form = useZodForm(groupSchema, { defaultValues: { groupId: NO_GROUP, newGroup: '' } });

  const submit = async (values: z.output<typeof groupSchema>) => {
    // A new group is created and joined together: creating a folder and leaving the conversation out of it
    // would be a surprising half-action.
    const created = values.newGroup ? await createGroup.mutateAsync({ body: { name: values.newGroup } }) : null;
    const groupId = created?.id ?? (values.groupId === NO_GROUP ? null : values.groupId);
    await update.mutateAsync({ path: { id: conversationId }, body: { groupId } });
    onMoved();
    onClose();
  };

  return (
    <Modal open onClose={onClose} title="Move to group" maxWidth="sm">
      {/* A failed fetch must not read as "no groups yet": the next move would be a duplicate of an existing one. */}
      <QueryBoundary query={groups} label="groups">
        {(loaded) => (
          <Form form={form} onSubmit={submit}>
            <SelectFormField
              name="groupId"
              label="Group"
              options={[
                { value: NO_GROUP, label: 'No group' },
                ...loaded.data.map((group) => ({ value: group.id, label: group.name })),
              ]}
            />
            <TextFormField
              name="newGroup"
              label="Or create a new group"
              placeholder="Group name"
              description="Leave empty to use the group above."
              autoComplete="off"
            />
            <FormActions submitLabel="Move" onCancel={onClose} />
          </Form>
        )}
      </QueryBoundary>
    </Modal>
  );
}
